/**
 * Devices Modal Component
 *
 * Modal for creating and editing devices.
 */

import { useState, useEffect } from 'react';
import { Modal, Button, Form, Alert } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import RefreshButton from '@/components/common/RefreshButton';
import type { Device } from '@/types/associations';

interface DevicesModalProps {
  show: boolean;
  device?: Device | null;
  onHide: () => void;
  onSave: (data: Partial<Device>) => Promise<void>;
  onRefresh?: () => Promise<void>;
}

export default function DevicesModal({ show, device, onHide, onSave, onRefresh }: DevicesModalProps) {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    device_id: '',
    name: '',
    model: '',
    manufacturer: '',
    description: '',
  });
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (device) {
      setFormData({
        device_id: device.device_id || '',
        name: device.name || '',
        model: device.model || '',
        manufacturer: device.manufacturer || '',
        description: device.description || '',
      });
    } else {
      setFormData({
        device_id: '',
        name: '',
        model: '',
        manufacturer: '',
        description: '',
      });
    }
    setError(null);
  }, [device, show]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsSaving(true);

    try {
      await onSave(formData);
      onHide();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('admin.policyDatasets.saveFailed', { type: 'device' }));
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} centered scrollable>
      <Modal.Header closeButton>
        <div className="d-flex justify-content-between align-items-center w-100 me-3">
          <Modal.Title>{device ? t('admin.policyDatasets.editDevice') : t('admin.policyDatasets.createDevice')}</Modal.Title>
          {onRefresh && <RefreshButton onRefresh={onRefresh} size="sm" variant="outline-secondary" />}
        </div>
      </Modal.Header>
      <Modal.Body>
        <Form id="devices-form" onSubmit={handleSubmit}>
          {error && (
            <Alert variant="danger" dismissible onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          <Form.Group className="mb-3">
            <Form.Label>
              {t('admin.policyDatasets.deviceId')} <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              type="text"
              value={formData.device_id}
              onChange={(e) => setFormData({ ...formData, device_id: e.target.value })}
              placeholder={t('admin.policyDatasets.deviceIdPlaceholder')}
              required
              autoFocus
              disabled={!!device}
            />
            <Form.Text className="text-muted">
              {device ? t('admin.policyDatasets.deviceIdCannotChange') : t('admin.policyDatasets.deviceIdRequired')}
            </Form.Text>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>{t('admin.policyDatasets.description')}</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder={t('admin.policyDatasets.descriptionPlaceholder')}
            />
            <Form.Text className="text-muted">{t('admin.policyDatasets.descriptionOptional')}</Form.Text>
          </Form.Group>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide} disabled={isSaving}>
          {t('common.cancel')}
        </Button>
        <Button variant="primary" type="submit" form="devices-form" disabled={isSaving}>
          {isSaving ? t('admin.policyDatasets.saving') : device ? t('common.update') : t('common.create')}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
